import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { ArrowLeft, Heart, Star, Hammer, Youtube, Download, ExternalLink, Bookmark, Share2, MessageCircle, Send, Trash2, X, Camera, Image, FileText } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = {
  hive_plans: 'Hive Plans', apiary_layouts: 'Apiary Layouts', honey_house: 'Honey House',
  equipment_diy: 'Equipment DIY', swarm_traps: 'Swarm Traps', frames_foundation: 'Frames & Foundation',
  extraction: 'Extraction & Processing', feeding: 'Feeding & Nutrition', pest_management: 'Pest Management',
  transport: 'Transport & Moving', protective_gear: 'Protective Gear', garden_pollination: 'Garden & Pollination',
  tech_gadgets: 'Tech & Gadgets', other: 'Other', general: 'General',
};
const DIFF = { easy: 'Easy', moderate: 'Moderate', advanced: 'Advanced', expert: 'Expert' };
const TIME = { '<1hr': '< 1 Hour', half_day: 'Half Day', full_day: 'Full Day', weekend: 'Weekend', multi_day: 'Multi-Day' };

const Stars = ({ count, size = 'h-4 w-4' }) => (
  <span className="flex">{[1,2,3,4,5].map(i => <Star key={i} className={`${size} ${i <= count ? 'text-amber-500 fill-amber-500' : 'text-stone-300'}`} />)}</span>
);
const StarInput = ({ value, onChange }) => (
  <div className="flex gap-1">{[1,2,3,4,5].map(i => (
    <button key={i} type="button" onClick={() => onChange(i)} className="p-0.5">
      <Star className={`h-6 w-6 transition ${i <= value ? 'text-amber-500 fill-amber-500' : 'text-stone-300 hover:text-amber-400'}`} />
    </button>
  ))}</div>
);

const IdeaDetail = () => {
  const params = useParams();
  const [searchParams] = useSearchParams();
  const ideaId = params.ideaId || searchParams.get('ideaId');
  const navigate = useNavigate();
  const { user, isDemo } = useAuth();
  const [idea, setIdea] = useState(null);
  const [loading, setLoading] = useState(true);
  const [comments, setComments] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [madeIts, setMadeIts] = useState([]);
  const [commentText, setCommentText] = useState('');
  const [reviewStars, setReviewStars] = useState(0);
  const [reviewText, setReviewText] = useState('');
  const [galleryIdx, setGalleryIdx] = useState(0);
  const [isBookmarked, setIsBookmarked] = useState(false);
  // Made It form
  const [showMadeIt, setShowMadeIt] = useState(false);
  const [madePhotos, setMadePhotos] = useState([]);
  const [madeCaption, setMadeCaption] = useState('');
  const [madeStars, setMadeStars] = useState(0);
  const [lightboxPhoto, setLightboxPhoto] = useState(null);

  const token = localStorage.getItem('token');
  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  const fetchIdea = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/diy-ideas/${ideaId}`, { headers });
      setIdea(res.data);
    } catch { setIdea(null); }
    setLoading(false);
  }, [ideaId]);

  const fetchComments = useCallback(async () => {
    try { const res = await axios.get(`${API}/diy-ideas/${ideaId}/comments`, { headers }); setComments(res.data || []); } catch {}
  }, [ideaId]);

  const fetchReviews = useCallback(async () => {
    try { const res = await axios.get(`${API}/diy-ideas/${ideaId}/reviews`, { headers }); setReviews(res.data || []); } catch {}
  }, [ideaId]);

  const fetchMadeIts = useCallback(async () => {
    try { const res = await axios.get(`${API}/diy-ideas/${ideaId}/made-it`, { headers }); setMadeIts(res.data || []); } catch {}
  }, [ideaId]);

  useEffect(() => {
    fetchIdea(); fetchComments(); fetchReviews(); fetchMadeIts();
    // Check bookmark
    if (token) {
      axios.get(`${API}/diy-ideas/user/bookmarks`, { headers: { Authorization: `Bearer ${token}` } })
        .then(res => setIsBookmarked((res.data || []).includes(ideaId))).catch(() => {});
    }
  }, [fetchIdea, fetchComments, fetchReviews, fetchMadeIts, ideaId, token]);

  const getPhotoPath = (p) => typeof p === 'string' ? p : p?.path || '';
  const getPhotoCaption = (p) => typeof p === 'object' ? p?.caption || '' : '';

  if (loading) return <div className="bg-amber-50 flex items-center justify-center text-stone-400">Loading...</div>;
  if (!idea) return (
    <div className="bg-amber-50 flex items-center justify-center">
      <div className="text-center space-y-3">
        <p className="text-stone-500">Idea not found.</p>
        <button onClick={() => navigate('/?view=diy-ideas')} className="text-amber-600 text-sm font-medium hover:underline">Back to DIY Ideas</button>
      </div>
    </div>
  );

  const photos = idea.photos || [];
  const planFile = idea.plan_file;
  const ytId = ((idea.youtube_url || '').match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\s?]+)/) || [])[1];
  const isLiked = (idea.liked_by || []).includes(user?.id);

  const toggleLike = async () => {
    if (isDemo) return;
    try {
      const res = await axios.post(`${API}/diy-ideas/${ideaId}/like`, {}, { headers: { Authorization: `Bearer ${token}` } });
      setIdea(prev => ({ ...prev, likes: res.data.liked ? (prev.likes||0)+1 : Math.max((prev.likes||0)-1,0), liked_by: res.data.liked ? [...(prev.liked_by||[]), user?.id] : (prev.liked_by||[]).filter(x => x !== user?.id) }));
    } catch {}
  };

  const toggleBookmark = async () => {
    if (isDemo) return;
    try {
      const res = await axios.post(`${API}/diy-ideas/${ideaId}/bookmark`, {}, { headers: { Authorization: `Bearer ${token}` } });
      setIsBookmarked(res.data.bookmarked);
    } catch {}
  };

  const postComment = async () => {
    if (!commentText.trim()) return;
    try {
      const res = await axios.post(`${API}/diy-ideas/${ideaId}/comments`, { text: commentText.trim() }, { headers: { Authorization: `Bearer ${token}` } });
      setComments(prev => [...prev, res.data]); setCommentText('');
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const submitReview = async () => {
    if (reviewStars < 1) { toast.error('Select a star rating'); return; }
    try {
      await axios.post(`${API}/diy-ideas/${ideaId}/review`, { stars: reviewStars, text: reviewText.trim() }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Review submitted'); fetchReviews(); fetchIdea(); setReviewStars(0); setReviewText('');
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const uploadMadePhoto = async (e) => {
    for (const file of Array.from(e.target.files)) {
      if (madePhotos.length >= 5) break;
      try {
        const fd = new FormData(); fd.append('file', file);
        const res = await axios.post(`${API}/diy-ideas/upload`, fd, { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' } });
        setMadePhotos(prev => [...prev, { path: res.data.path }]);
      } catch {}
    }
    e.target.value = '';
  };

  const submitMadeIt = async () => {
    try {
      await axios.post(`${API}/diy-ideas/${ideaId}/made-it`, { photos: madePhotos.map(p => ({ path: p.path })), caption: madeCaption, stars: madeStars }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Build shared!'); setShowMadeIt(false); setMadePhotos([]); setMadeCaption(''); setMadeStars(0); fetchMadeIts(); fetchIdea();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const shareIdea = () => {
    const url = window.location.href;
    if (navigator.share) { navigator.share({ title: idea.title, text: idea.description?.slice(0, 100), url }); }
    else { navigator.clipboard.writeText(url); toast.success('Link copied!'); }
  };

  return (
    <div className="bg-gradient-to-br from-amber-50 via-orange-50 to-yellow-50">
      <div className="max-w-3xl mx-auto p-4 sm:p-6 pb-24">
        <button onClick={() => navigate('/?view=diy-ideas')} className="flex items-center gap-2 text-stone-600 hover:text-stone-800 mb-4 text-sm">
          <ArrowLeft className="h-4 w-4" /> Back to DIY Ideas
        </button>

        {/* Photo Gallery */}
        {photos.length > 0 && (
          <div className="bg-white rounded-2xl border border-stone-200 shadow-sm overflow-hidden mb-4">
            <div className="relative bg-stone-100">
              <img src={`${API}/files/${getPhotoPath(photos[galleryIdx])}`} alt="" className="w-full max-h-[28rem] object-contain" />
              {photos.length > 1 && <div className="absolute bottom-3 right-3 bg-black/60 text-white text-sm px-3 py-1 rounded-full">{galleryIdx + 1} / {photos.length}</div>}
            </div>
            {getPhotoCaption(photos[galleryIdx]) && (
              <div className="px-4 py-2 bg-stone-50 text-sm text-stone-600 text-center border-t border-stone-100">{getPhotoCaption(photos[galleryIdx])}</div>
            )}
            {photos.length > 1 && (
              <div className="flex gap-2 p-3 overflow-x-auto">
                {photos.map((p, i) => (
                  <img key={i} src={`${API}/files/${getPhotoPath(p)}`} alt="" onClick={() => setGalleryIdx(i)}
                    className={`h-16 w-16 rounded-lg object-cover flex-shrink-0 cursor-pointer border-2 transition ${i === galleryIdx ? 'border-amber-500' : 'border-transparent hover:border-stone-300'}`} />
                ))}
              </div>
            )}
          </div>
        )}

        {/* YouTube embed */}
        {ytId && (
          <div className="bg-white rounded-2xl border border-stone-200 shadow-sm overflow-hidden mb-4">
            <iframe src={`https://www.youtube.com/embed/${ytId}`} className="w-full h-64" allowFullScreen title="Video" />
          </div>
        )}

        {/* Main Content */}
        <div className="bg-white rounded-2xl border border-stone-200 shadow-sm p-6 mb-4">
          <div className="flex items-start justify-between gap-3 mb-3">
            <h1 className="text-xl font-bold text-stone-800">{idea.title}</h1>
            <span className="text-xs px-2.5 py-1 rounded-full border font-medium bg-amber-100 text-amber-700 border-amber-200 flex-shrink-0">{CATEGORIES[idea.category] || idea.category}</span>
          </div>

          {/* Builder Ratings */}
          {(idea.difficulty || idea.cost_rating || idea.time_estimate) && (
            <div className="flex flex-wrap gap-2 mb-3">
              {idea.difficulty && <span className="text-xs px-2.5 py-1 rounded-full font-medium bg-green-100 text-green-700">{DIFF[idea.difficulty] || idea.difficulty}</span>}
              {idea.cost_rating && <span className="text-xs px-2.5 py-1 rounded-full font-medium bg-emerald-100 text-emerald-700">{idea.cost_rating}</span>}
              {idea.time_estimate && <span className="text-xs px-2.5 py-1 rounded-full font-medium bg-blue-100 text-blue-700">{TIME[idea.time_estimate] || idea.time_estimate}</span>}
            </div>
          )}

          {/* Star Rating */}
          {idea.review_count > 0 && (
            <div className="flex items-center gap-2 mb-3">
              <Stars count={Math.round(idea.review_avg)} /> <span className="text-sm text-stone-600">{idea.review_avg} from {idea.review_count} reviews</span>
              {idea.made_it_count > 0 && <span className="text-sm text-amber-600 ml-2"><Hammer className="h-4 w-4 inline" /> {idea.made_it_count} made it</span>}
            </div>
          )}

          {idea.description && <p className="text-sm text-stone-600 mb-4 whitespace-pre-wrap">{idea.description}</p>}

          {/* Tags */}
          {idea.tags?.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mb-4">
              {idea.tags.map(t => <span key={t} className="text-xs bg-stone-100 text-stone-600 px-2 py-0.5 rounded">#{t}</span>)}
            </div>
          )}

          {/* Links */}
          <div className="flex items-center gap-4 mb-4">
            {idea.youtube_url && <a href={idea.youtube_url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 text-sm text-red-600 hover:text-red-700"><Youtube className="h-4 w-4" /> Watch Video <ExternalLink className="h-3.5 w-3.5" /></a>}
            {planFile && <a href={`${API}/files/${planFile.path}`} download={planFile.filename} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 text-sm text-blue-600 hover:text-blue-700"><Download className="h-4 w-4" /> Download Plan</a>}
          </div>

          {/* Actions */}
          <div className="flex items-center gap-3 pt-3 border-t border-stone-100">
            <button onClick={toggleLike} className={`flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-full transition ${isLiked ? 'bg-red-50 text-red-600' : 'text-stone-500 hover:bg-red-50 hover:text-red-500'}`}>
              <Heart className={`h-4 w-4 ${isLiked ? 'fill-current' : ''}`} /> {idea.likes || 0}
            </button>
            <button onClick={toggleBookmark} className={`flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-full transition ${isBookmarked ? 'bg-amber-50 text-amber-600' : 'text-stone-500 hover:bg-amber-50'}`}>
              <Bookmark className={`h-4 w-4 ${isBookmarked ? 'fill-current' : ''}`} /> {isBookmarked ? 'Saved' : 'Save'}
            </button>
            {idea.allow_sharing && (
            <button onClick={shareIdea} className="flex items-center gap-1.5 text-sm text-stone-500 hover:text-blue-600 px-3 py-1.5 rounded-full hover:bg-blue-50 transition">
              <Share2 className="h-4 w-4" /> Share
            </button>
            )}
          </div>

          {/* Author */}
          <div className="mt-4 pt-3 border-t border-stone-100 flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-amber-100 flex items-center justify-center text-amber-700 font-bold text-sm">{(idea.author_name || '?').charAt(0).toUpperCase()}</div>
            <div>
              <span className="text-sm font-medium text-amber-700 cursor-pointer hover:underline" onClick={() => navigate(`/?view=builder&userId=${idea.user_id}`)}>{idea.author_name || 'Anonymous'}</span>
              {idea.author_company && <span className="text-xs text-stone-400 ml-2">{idea.author_company}</span>}
            </div>
          </div>
        </div>

        {/* Made It! Section */}
        <div className="bg-white rounded-2xl border border-stone-200 shadow-sm p-5 mb-4">
          <h2 className="font-bold text-stone-800 flex items-center gap-2 mb-3"><Hammer className="h-5 w-5 text-amber-600" /> Made It! ({madeIts.length})</h2>
          {madeIts.map(m => (
            <div key={m.id} className="bg-amber-50 rounded-xl px-4 py-3 mb-2 border border-amber-100">
              <div className="flex items-center justify-between text-sm">
                <span className="font-semibold text-amber-800 cursor-pointer hover:underline" onClick={() => navigate(`/?view=builder&userId=${m.user_id}`)}>{m.author_name}</span>
                {m.stars > 0 && <Stars count={m.stars} size="h-3.5 w-3.5" />}
              </div>
              {m.caption && <p className="text-sm text-stone-600 mt-1">{m.caption}</p>}
              {m.photos?.length > 0 && <div className="flex gap-2 mt-2 overflow-x-auto">{m.photos.map((ph, i) => <img key={i} src={`${API}/files/${typeof ph === 'string' ? ph : ph?.path || ''}`} alt="" className="h-20 w-20 rounded-lg object-cover cursor-pointer hover:opacity-80 transition" onClick={() => setLightboxPhoto(`${API}/files/${typeof ph === 'string' ? ph : ph?.path || ''}`)} />)}</div>}
            </div>
          ))}
          {!isDemo && !showMadeIt && (
            <button onClick={() => setShowMadeIt(true)} className="flex items-center gap-1.5 text-sm text-amber-600 font-medium hover:text-amber-700 mt-2">
              <Camera className="h-4 w-4" /> I Made This!
            </button>
          )}
          {showMadeIt && (
            <div className="bg-amber-50 rounded-xl p-4 mt-2 border border-amber-200 space-y-3">
              <div className="flex gap-2 items-center">
                <input type="file" accept="image/*" multiple onChange={uploadMadePhoto} className="hidden" id="made-it-detail-upload" />
                <label htmlFor="made-it-detail-upload" className="cursor-pointer text-xs bg-amber-600 text-white px-3 py-1.5 rounded-full font-medium hover:bg-amber-700">Upload Photos ({madePhotos.length}/5)</label>
              </div>
              {madePhotos.length > 0 && <div className="flex gap-2">{madePhotos.map((p,i) => <div key={i} className="relative"><img src={`${API}/files/${p.path}`} alt="" className="h-14 w-14 rounded object-cover" /><button onClick={() => setMadePhotos(prev => prev.filter((_,idx) => idx !== i))} className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full p-0.5"><X className="h-3 w-3" /></button></div>)}</div>}
              <Input value={madeCaption} onChange={e => setMadeCaption(e.target.value)} placeholder="How did it go?" className="h-9 text-sm" />
              <div className="flex items-center gap-2"><span className="text-sm text-stone-600">Rate:</span><StarInput value={madeStars} onChange={setMadeStars} /></div>
              <div className="flex gap-2"><Button onClick={submitMadeIt} className="bg-amber-600 hover:bg-amber-700 text-white rounded-full text-sm">Share Build</Button><Button variant="ghost" onClick={() => setShowMadeIt(false)} className="text-sm">Cancel</Button></div>
            </div>
          )}
        </div>

        {/* Reviews */}
        <div className="bg-white rounded-2xl border border-stone-200 shadow-sm p-5 mb-4">
          <h2 className="font-bold text-stone-800 flex items-center gap-2 mb-3"><Star className="h-5 w-5 text-amber-500" /> Reviews ({reviews.length})</h2>
          {reviews.map(r => (
            <div key={r.id} className="border-b border-stone-100 last:border-0 py-2">
              <div className="flex items-center justify-between"><span className="text-sm font-medium text-stone-700">{r.author_name}</span><Stars count={r.stars} size="h-3.5 w-3.5" /></div>
              {r.text && <p className="text-xs text-stone-500 mt-0.5">{r.text}</p>}
            </div>
          ))}
          {!isDemo && (
            <div className="mt-3 pt-3 border-t border-stone-100 space-y-2">
              <div className="flex items-center gap-2"><span className="text-sm text-stone-600">Your rating:</span><StarInput value={reviewStars} onChange={setReviewStars} /></div>
              <Input value={reviewText} onChange={e => setReviewText(e.target.value)} placeholder="Write a review (optional)" className="h-9 text-sm" />
              <Button onClick={submitReview} disabled={reviewStars < 1} className="bg-amber-600 hover:bg-amber-700 text-white rounded-full text-sm">Submit Review</Button>
            </div>
          )}
        </div>

        {/* Comments */}
        <div className="bg-white rounded-2xl border border-stone-200 shadow-sm p-5">
          <h2 className="font-bold text-stone-800 flex items-center gap-2 mb-3"><MessageCircle className="h-5 w-5 text-stone-500" /> Comments ({comments.length})</h2>
          {comments.map(c => (
            <div key={c.id} className="bg-stone-50 rounded-lg px-3 py-2 mb-2 text-sm">
              <div className="flex items-center justify-between"><span className="font-semibold text-stone-700">{c.author_name}</span><span className="text-xs text-stone-400">{(c.created_at||'').split('T')[0]}</span></div>
              <p className="text-stone-600 mt-0.5 whitespace-pre-wrap">{c.text}</p>
            </div>
          ))}
          {!isDemo && (
            <div className="flex gap-2 mt-2">
              <Input value={commentText} onChange={e => setCommentText(e.target.value)} placeholder="Ask a question or share a tip..." className="h-9 text-sm flex-1" onKeyDown={e => e.key === 'Enter' && postComment()} />
              <button onClick={postComment} disabled={!commentText.trim()} className="h-9 px-4 bg-amber-600 text-white rounded-lg text-sm font-medium hover:bg-amber-700 disabled:opacity-50 flex items-center gap-1"><Send className="h-3.5 w-3.5" /></button>
            </div>
          )}
        </div>
      </div>

      {/* Single Photo Lightbox (for Made It photos) */}
      {lightboxPhoto && (
        <div className="fixed inset-0 bg-black/85 z-50 flex items-center justify-center p-4" onClick={() => setLightboxPhoto(null)} data-testid="photo-lightbox">
          <div className="relative max-w-3xl w-full" onClick={e => e.stopPropagation()}>
            <button onClick={() => setLightboxPhoto(null)} className="absolute -top-10 right-0 text-white hover:text-stone-300"><X className="h-6 w-6" /></button>
            <img src={lightboxPhoto} alt="" className="w-full max-h-[80vh] object-contain rounded-xl" />
          </div>
        </div>
      )}
    </div>
  );
};

export default IdeaDetail;
